import React, {useRef, forwardRef, useImperativeHandle} from 'react';

import EmailReceiptForm, {
  EmailReceiptFormRef,
  EmailReceiptInfo,
} from './EmailReceiptForm';
import PhoneReceiptForm, {
  PhoneReceiptFormRef,
  PhoneReceiptInfo,
} from './PhoneReceiptForm';
import Call from '../../../../assets/icons/call.svg';
import Sms from '../../../../assets/icons/sms.svg';
import CustomRadioInput from '../../../../components/common/CustomRadioInput';

export type ReceiptMethod = 'email' | 'phone';
const ENABLED_RECEIPT_METHODS = ['email', 'phone'] as const;

const RECEIPT_METHOD_DETAILS: Record<
  string,
  {label: string; icon?: JSX.Element; logo?: JSX.Element}
> = {
  email: {
    label: 'Email',
    icon: <img src={Sms} alt="Email" className="h-4 mr-2" />,
    logo: undefined,
  },
  phone: {
    label: 'Phone Number',
    icon: <img src={Call} alt="Phone" className="h-4 mr-2" />,
    logo: undefined,
  },
};

// Export ref interface for parent to access validation
export interface ReceiptMethodSelectorRef {
  validateCurrentMethod: () => boolean;
  hasErrors: () => boolean;
}

const ReceiptMethodSelector = forwardRef<
  ReceiptMethodSelectorRef,
  {
    receiptMethod: ReceiptMethod;
    setReceiptMethod: React.Dispatch<React.SetStateAction<ReceiptMethod>>;
    emailReceiptInfo: EmailReceiptInfo;
    setEmailReceiptInfo: React.Dispatch<React.SetStateAction<EmailReceiptInfo>>;
    phoneReceiptInfo: PhoneReceiptInfo;
    setPhoneReceiptInfo: React.Dispatch<React.SetStateAction<PhoneReceiptInfo>>;
  }
>((props, ref) => {
  const {
    receiptMethod,
    setReceiptMethod,
    emailReceiptInfo,
    setEmailReceiptInfo,
    phoneReceiptInfo,
    setPhoneReceiptInfo,
  } = props;

  // Refs to access child component validation
  const emailReceiptFormRef = useRef<EmailReceiptFormRef>(null);
  const phoneReceiptFormRef = useRef<PhoneReceiptFormRef>(null);

  // Expose validation methods to parent
  useImperativeHandle(ref, () => ({
    validateCurrentMethod: () => {
      if (receiptMethod === 'email') {
        return emailReceiptFormRef.current
          ? emailReceiptFormRef.current.validateAll()
          : false;
      } else if (receiptMethod === 'phone') {
        return phoneReceiptFormRef.current
          ? phoneReceiptFormRef.current.validateAll()
          : false;
      }
      return false;
    },

    hasErrors: () => {
      if (receiptMethod === 'email') {
        return emailReceiptFormRef.current
          ? emailReceiptFormRef.current.hasErrors()
          : false;
      } else if (receiptMethod === 'phone') {
        return phoneReceiptFormRef.current
          ? phoneReceiptFormRef.current.hasErrors()
          : false;
      }
      return false;
    },
  }));

  return (
    <div className="rounded-lg mt-3 outline outline-[1px] outline-offset-[-0.80px] outline-subtleGrey inline-flex flex-col justify-start items-start overflow-hidden w-full">
      {ENABLED_RECEIPT_METHODS.map((item, idx) => (
        <div
          key={item}
          className={`p-4 w-full ${
            idx !== ENABLED_RECEIPT_METHODS.length - 1
              ? 'border-b-[1px] border-subtleGrey'
              : ''
          }`}>
          <CustomRadioInput
            checked={receiptMethod === item}
            handleChange={() => setReceiptMethod(item)}
            item={item}
            methodDetails={RECEIPT_METHOD_DETAILS}
          />
          {receiptMethod === 'email' && item === 'email' && (
            <EmailReceiptForm
              ref={emailReceiptFormRef}
              emailReceiptInfo={emailReceiptInfo}
              setEmailReceiptInfo={setEmailReceiptInfo}
            />
          )}
          {receiptMethod === 'phone' && item === 'phone' && (
            <PhoneReceiptForm
              ref={phoneReceiptFormRef}
              phoneReceiptInfo={phoneReceiptInfo}
              setPhoneReceiptInfo={setPhoneReceiptInfo}
            />
          )}
        </div>
      ))}
    </div>
  );
});

ReceiptMethodSelector.displayName = 'ReceiptMethodSelector';

export default ReceiptMethodSelector;
